import {
  FontWeights,
  ILabelStyles,
  ILinkStyles,
  IProcessedStyleSet,
  IStyle,
  concatStyleSets,
  mergeStyleSets
} from 'office-ui-fabric-react';
import { HostAppSettings, HostAppSettingName } from '../../../backend/office-js/HostAppSettings';
import { HostAppUtil } from '../../../backend/office-js/HostAppUtil';
import { AppColor, AppColorContainer } from '../colors/AppColorContainer';
import { AppThemeFactoryProducer } from '../colors/AppThemeFactoryProducer';

export namespace AppLayoutSettings {

  export namespace AppImageParams {

    /** Width [in pixels] of the cartoon image, to be shown on Transcription Pane. */
    export const cartoonImageWidthPx = '200px';

      /** Width [in pixels] of the cartoon image, to be shown on Transcription Pane. */
      export const cartoonImageWidthv2Px = '170px';

    /** Height [in pixels] of the cartoon image, to be shown on Transcription Pane. */
    export const cartoonImageHeightPx = '142px';

    /** Width [in pixels] of the failure image, to be shown on Transcription Pane. */
    export const failureImageWidthPx = "142px";

    /** Height [in pixels] of the failure image, to be shown on Transcription Pane. */
    export const failureImageHeightPx = "131px";

    /** Name of the failure image, to be shown on Transcription Pane. */
    export const failureImageName = 'Illustration-Error.svg';

    /** Name of the image in Microphone Permissions Dialog, to be shown on Transcription Pane. */
    export const microphonePermissionImageName = 'MicPermissionsDialogImage.svg';

     /** Width [in pixels] of the image in Microphone Permissions Dialog, to be shown on Transcription Pane. */
    export const microphonePermissionWidthPx = "252px";

     /** Height [in pixels] of the image in Microphone Permissions Dialog, to be shown on Transcription Pane. */
    export const microphonePermissionHeightPx = "51px";

     /** Width [in pixels] of the cartoon image, to be shown on Transcription Pane. */
     export const MicrosoftImageWidthPx = '19px';

     /** Height [in pixels] of the cartoon image, to be shown on Transcription Pane. */
     export const MicrosoftImageHeightPx = '19px';

     /** Name of the cartoon image, to be shown on Transcription Pane. */
     export const MicrosoftImageName = 'MicrosoftLogo.svg';
  }

  /** This is the color interface we should call from setting up color styling for all the transcription components.
   * This interface handles the color adjustments for dark and light modes accordingly.
   * Usage example:
   * const colorContainer = AppLayoutSettings.getAppColorContainer()
   * const colorValue = colorContainer.darkLabelColor.value
  */
  export function getAppColorContainer(): AppColorContainer {
    const isSharedUXDarkModeOn: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.IsSharedUXDarkModeOn);

    // Provides us Word/OneNote Factory object on the basis of current host app name.
    const appThemeFactory = AppThemeFactoryProducer.GetInstance(HostAppUtil.GetHostAppName());

    // Calling GetThemeColorContainer to get AppColorContainer specific to IsSharedUXDarkModeOn value and host app factory.
    return appThemeFactory.GetThemeColorContainer(isSharedUXDarkModeOn);
  }

  export enum AppFontSize {

    /** Alternate font size in pixels for extra large font display. */
    extraLargePlusFontSizePx = '17px',

    /** Font size in pixels for extra large font display. */
    extraLargeFontSizePx = '16px',

    /** Alternate font size in pixels for large font display. */
    largePlusFontSizePx = '15px',

    /** Font size in pixels for large font display. */
    largeFontSizePx = '14px',

    /** Font size in pixels for medium font display. */
    mediumFontSizePx = '12px',

    /** Alternate font size in pixels for small font display. */
    smallPlusFontSizePx = '11px',

    /** Font size in pixels for small font display. */
    smallFontSizePx = '10px',

    /** Font size in pixels for extra small font display. */
    extraSmallFontSizePx = '9px'
  }

  export enum AppDisplayType {

    /** Displays an element as a block element (like <p>). */
    block = 'block',

    /** Displays an element as an inline element (like <span>). */
    inline = 'inline',

    /** Displays an element as an inline-level block container. */
    inlineBlock = 'inline-block',

    /** Displays an element as a block-level flex container. */
    flex = 'flex',

    /** Inherits this property from its parent element. */
    inherit = 'inherit',

    /** The element is hidden. */
    none = 'none'
  }

  export namespace AppCommonStyles {

    export const getPageRootContainersCSSProperties = (): React.CSSProperties => {
      /** Common padding, in pixels, to be used across containers. */
      const containerSidesPaddingSizePx = 16;

      const CSSProperties: React.CSSProperties = {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: `${containerSidesPaddingSizePx}px`,
        paddingRight: `${containerSidesPaddingSizePx}px`,
        flexDirection: 'column',
        display: 'flex',
        height: '100%',
      };

      return CSSProperties;
    };

    export const getLabelStyles = (fontSizePx: AppFontSize, fontColor: AppColor): ILabelStyles => {
      const styles: ILabelStyles = {
        root: {
          fontSize: fontSizePx,
          color: fontColor.value
        }
      };

      return styles;
    };

    export const getRegularLabelStyles = (fontSizePx: AppFontSize, fontColor: AppColor): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getLabelStyles(fontSizePx, fontColor);
      const regularLabelStyles: ILabelStyles = {
        root: {
          fontWeight: FontWeights.regular
        }
      };

      return concatStyleSets(commonLabelStyles, regularLabelStyles);
    };

    export const getRegularTagLabelStyles = (): ILabelStyles => {
      const regularLabelStyles: ILabelStyles = {
        root: {
          position: 'relative',
          top: '8px',
          paddingTop: '2px',
          paddingLeft: '4px',
          paddingBottom: '2px',
          paddingRight: '4px',
          fontFamily: 'Segoe UI',
          fontSize : AppFontSize.extraSmallFontSizePx,
          display : 'center',
          alignItems: 'flex-end',
          color: getAppColorContainer().whiteColor.value
        }
      };

      return regularLabelStyles;
    };

    export const getBoldLabelStyles = (fontSizePx: AppFontSize, fontColor: AppColor): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getLabelStyles(fontSizePx, fontColor);
      const boldLabelStyles: ILabelStyles = {
        root: {
          fontWeight: FontWeights.semibold
        }
      };

      return concatStyleSets(commonLabelStyles, boldLabelStyles);
    };

    export const getPageTopLabelStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().mediumLabelColor);
      const specificStyle: ILabelStyles = {
        root: {
          paddingTop: '0px'
        }
      };

      return concatStyleSets(commonLabelStyles, specificStyle);
    };

    export const getLinkStyles = (fontSizePx: AppFontSize, fontColor: AppColor): ILinkStyles => {
      const styles: ILinkStyles = {
        root: {
          fontSize: fontSizePx,
          fontWeight: FontWeights.semibold,
          color: fontColor.value,
          background: 'transparent !important',
          border: '0px !important',
          outline: 'none !important',
          borderRadius: '0px !important',
          borderWidth: '0px !important',
          outlineColor: 'none !important',
          selectors: {
            [`:focus`]: {
              color: fontColor.value,
              outline: '1px solid',
              outlineColor: AppLayoutSettings.getAppColorContainer().keyboardFocusColor.value
            },
            [`:active , :hover`]: {
              color: fontColor.value,
              textDecoration: 'underline'
            }
          }
        }
      };

      return styles;
    };

    export const getFileInputStyles = (displayType: AppDisplayType) => {
      const styles = {
        display: displayType
      }

      return styles;
    };

    export const getLearnMoreLinkStyles = (): ILinkStyles => {
      return AppLayoutSettings.AppCommonStyles.getLinkStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().linkColor);
    };

    export const getTagStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularTagLabelStyles();
      const specificStyle: ILabelStyles = {
        root: {
          backgroundColor: AppLayoutSettings.getAppColorContainer().tagBlue.value,
          width: '60px',
          height: '20px',
        }
      };

      return concatStyleSets(commonLabelStyles, specificStyle);
    };

    export const getTranscriptLabelStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.largeFontSizePx, AppLayoutSettings.getAppColorContainer().charcoalColor);
      const specificStyle: ILabelStyles = {
        root: {
          paddingTop: '12px',
          fontFamily: 'Segoe UI',
          lineHeight: '22px',
          fontWeight: 600
        }
      };

      return concatStyleSets(commonLabelStyles, specificStyle);
    };

    export const getPreviewLabelStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.extraSmallFontSizePx, AppLayoutSettings.getAppColorContainer().whiteColor);
      const specificStyle: ILabelStyles = {
        root: {
          fontFamily: 'Segoe UI',
          fontWeight: 700,
          height: '12px',
          width: '42px'
        }
      };

      return concatStyleSets(commonLabelStyles, specificStyle);
    };

    export const getHeaderTopLabelStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.largeFontSizePx, AppLayoutSettings.getAppColorContainer().mediumLabelColor);
      const specificStyle: ILabelStyles = {
        root: {
          fontWeight: 600,
          paddingTop: '1px',
          textAlign: 'center'
        }
      };
      return concatStyleSets(commonLabelStyles, specificStyle);
    };
    export const getDescriptionLabelStyles = (): ILabelStyles => {
      const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().mediumLabelColor);
      const specificStyle: ILabelStyles = {
        root: {
          paddingTop: '2px',
          textAlign: 'center'
        }
      };
      return concatStyleSets(commonLabelStyles, specificStyle);
    };

  }
}

// Styles for the "main" (a.k.a. Landing) page.
// Used both for Landing and Failure pages.
export interface MainPageStyles {
  labelContainer: IStyle;
  buttonContainer: IStyle;
  imageContainer: IStyle;
  imageContainerv2: IStyle;
  bottomLabelContainer: IStyle;
  progressContainer: IStyle;
  bottomMinuteLimitInfoContainer : IStyle;
  bottomLabelContainerUnlimitedMins : IStyle;
  dropDownContainer: IStyle;
  buttonContainerv2: IStyle;
  transcriptDescriptionContainer: IStyle;
  transcriptContainer: IStyle;
  previewContainer: IStyle;
  transcriptionBottomContainer: IStyle;
  timeContainer: IStyle;
  learnMoreContainer: IStyle;
  noWordsContainer: IStyle;
  bottomLabelContainerv2: IStyle;
}

export const getMainPageStyles = (): IProcessedStyleSet<MainPageStyles> => {
  const styles: MainPageStyles = {
    labelContainer: {
      width: '100%'
    },
    buttonContainer: {
      marginTop: '24px',
      display: 'flex',
      justifyContent: 'space-around',
      width: '100%',
      whiteSpace: 'normal',
      flexWrap: 'wrap'
    },
    imageContainer: {
      marginTop: '50%',
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    },
    imageContainerv2: {
      marginTop: '20px',
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    },
    bottomLabelContainer: {
      paddingBottom: '28px'
    },
    bottomLabelContainerv2: {
      paddingBottom: '12px'
    },
    bottomLabelContainerUnlimitedMins: {
      position: 'relative',
      bottom: '8px'
    },
    progressContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      width: '50%'
    },
    bottomMinuteLimitInfoContainer : {
      position: 'relative',
      top: '8px'
    },
    dropDownContainer: {
      paddingTop: '11px'
    },
    buttonContainerv2: {
      paddingTop: '10px',
      display: 'flex',
      width: '100%',
      whiteSpace: 'normal',
      flexWrap: 'wrap'
    },
    transcriptDescriptionContainer: {
      paddingTop: '2px'
    },
    transcriptContainer: {
      width: '100%',
      height: '100%',
      overflowX: 'hidden',
      overflowY: 'hidden',
      paddingBottom: '8px'
    },
    previewContainer: {
      backgroundColor: '#2e6ac5',
      paddingBottom: '6px',
      width: '42px'
    },
    transcriptionBottomContainer: {
      display: 'flex'
    },
    timeContainer: {
      color: '#605E5C',
      fontSize: '12px',
      paddingRight: '5px'
    },
    learnMoreContainer: {
      paddingLeft: '6px'
    },
    noWordsContainer: {
      paddingTop: '120px',
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    },
  };

  return mergeStyleSets(styles);
};
